<script setup lang="ts">
import { computed, nextTick, ref, watch } from 'vue'
import { isAxiosError } from 'axios'
import { useQuasar } from 'quasar'
import { formatPhoneNumber } from '@/utils/phoneFormatter'
import moment from 'moment'
import { useI18n } from 'vue-i18n'
import { useRouter } from 'vue-router'
import { useFilterStore } from '@/stores/filterStore'
import { storeToRefs } from 'pinia'
import {
  getInvitesWithFilter,
  type InviteSortBy,
  type InviteSortOrder,
  type Invite,
  type InviteStatus
} from '@/api/endpoints/inviteWeb'
import type { PhoneTelNumberInputModel, VueTelInputData } from '@/types/phoneTelNumberInputTypes'

const filterStore = useFilterStore()
const { clearInviteFilterCount } = storeToRefs(filterStore)
const { t } = useI18n()
const router = useRouter()
const $q = useQuasar()

const inviteTable = ref()
const form = ref()
const loading = ref(false)
const showResults = ref(false)

const firstNameModel = ref('')
const lastNameModel = ref('')
const emailModel = ref('')
const statusModel = ref<InviteStatus | 'All'>('All')
const statusOptions = computed(() => [
  { label: t('invite_status_all'), value: 'All' as const },
  { label: t('invite_status_pending'), value: 'Pending' as const },
  { label: t('invite_status_redeemed'), value: 'Redeemed' as const },
  { label: t('invite_status_invalidated'), value: 'Invalidated' as const }
])
const phoneTelNumberInputModel = ref<PhoneTelNumberInputModel>({
  value: '',
  phoneObject: {} as VueTelInputData
})
const phoneInputRef = ref(null)

const inviteTableColumns = ref([
  { name: 'status', field: 'status', sortable: false, align: 'left' },
  { name: 'firstName', field: 'firstName', sortable: true, align: 'left' },
  { name: 'lastName', field: 'lastName', sortable: true, align: 'left' },
  { name: 'email', field: 'email', sortable: true, align: 'left' },
  { name: 'phoneNumber', field: 'phoneNumber', sortable: false, align: 'left' },
  { name: 'lastSentDate', field: 'lastSentDate', sortable: true, align: 'left' }
])
const inviteTableRows = ref<Invite[]>([])

const initialPagination = ref({
  sortBy: 'lastSentDate',
  descending: true,
  page: 1,
  rowsPerPage: 15,
  rowsNumber: 0
})

watch(clearInviteFilterCount, () => onCancel())

nextTick(() => form.value?.resetValidation())

const getSortBy = (sortBy: string): InviteSortBy => {
  switch (sortBy) {
    case 'firstName':
      return 'FirstName'
    case 'lastName':
      return 'LastName'
    case 'email':
      return 'Email'
    case 'lastSentDate':
    default:
      return 'LastSentDate'
  }
}

const getSortOrder = (descending: boolean): InviteSortOrder =>
  descending ? 'Desc' : 'Asc'

const normalizeOrNull = (value: string) => {
  const trimmed = value?.trim()
  return trimmed ? trimmed : null
}


const formatDate = (value: string | null) => {
  if (!value) {
    return ''
  }

  const parsed = moment(value)
  return parsed.isValid() ? parsed.format('LLLL') : value
}

const loadInvites = async (page: number, rowsPerPage: number, sortBy: string, descending: boolean) => {
  const sort = getSortBy(sortBy)
  $q.loading.show()
  try {
    const response = await getInvitesWithFilter({
      pageNumber: page,
      pageSize: rowsPerPage,
      sortBy: sort,
      sortOrder: getSortOrder(descending),
      firstName: normalizeOrNull(firstNameModel.value),
      lastName: normalizeOrNull(lastNameModel.value),
      email: normalizeOrNull(emailModel.value),
      status: statusModel.value === 'All' ? null : statusModel.value,
      phoneNumber: phoneTelNumberInputModel.value?.phoneObject?.nationalNumber ?? null,
      phoneCountryCode: null
    })

    const invites = response.data?.invites ?? []
    const mappedRows = invites.map((invite) => ({
      ...invite,
      lastSentDate: formatDate(invite.lastSentDate)
    }))

    inviteTableRows.value = mappedRows
    initialPagination.value.rowsNumber = response.data?.totalCount ?? 0
  } catch (error) {
    if (isAxiosError(error) && (error.response?.status === 401 || error.response?.status === 403)) {
      window.location.href = '/Logout'
    }
  } finally {
    $q.loading.hide()
  }
}

function getFilterCount() {
  let count = 0

  if (firstNameModel.value.trim() !== '') count++
  if (lastNameModel.value.trim() !== '') count++
  if (emailModel.value.trim() !== '') count++
  if (statusModel.value !== 'All') count++
  if (phoneTelNumberInputModel.value.value !== '') count++

  return count
}

const onSubmit = async () => {
  const isValid = await form.value.validate()

  if (!isValid) {
    return
  }

  loading.value = true
  filterStore.inviteFilterCount = getFilterCount()
  initialPagination.value.page = 1

  try {
    await loadInvites(
      initialPagination.value.page,
      initialPagination.value.rowsPerPage,
      initialPagination.value.sortBy,
      initialPagination.value.descending
    )
    showResults.value = true
  } finally {
    loading.value = false
  }
}

const onCancel = () => {
  firstNameModel.value = ''
  lastNameModel.value = ''
  emailModel.value = ''
  statusModel.value = 'All'
  phoneTelNumberInputModel.value.value = ''
  phoneTelNumberInputModel.value.phoneObject = {} as VueTelInputData

  inviteTableRows.value = []
  showResults.value = false

  initialPagination.value.sortBy = 'lastSentDate'
  initialPagination.value.descending = true
  initialPagination.value.page = 1
  initialPagination.value.rowsNumber = 0
  filterStore.inviteFilterCount = 0

  nextTick(() => form.value?.resetValidation())
}

const onRequest = async (props: any) => {
  const { page, rowsPerPage, sortBy, descending } = props.pagination

  loading.value = true

  try {
    await loadInvites(page, rowsPerPage, sortBy, descending)

    initialPagination.value.page = page
    initialPagination.value.sortBy = sortBy
    initialPagination.value.descending = descending
  } finally {
    loading.value = false
  }
}

const onRowClick = (inviteId: number) => {
  router.push({
    name: 'Invite',
    params: {
      inviteId: inviteId.toString()
    }
  })
}
</script>

<template>
  <q-page padding>
    <div class="q-pb-xl">
      <h1>{{ $t('gen_invites') }}</h1>
    </div>

    <div class="row">
      <div class="col bg-white q-pa-xl rep-profile__form">
        <q-form @submit.prevent ref="form">
          <zbm-skeleton-loader height="22.5714285714rem" width="100%">
            <div class="row mb-20 rep-profile__fields">
              <div class="col q-pa-sm">
                <q-input outlined v-model="firstNameModel" :placeholder="$t('gen_first_name')" />
              </div>
              <div class="col q-pa-sm">
                <q-input outlined v-model="lastNameModel" :placeholder="$t('gen_last_name')" />
              </div>
              <div class="col q-pa-sm">
                <q-input outlined v-model="emailModel" :placeholder="$t('gen_email')" />
              </div>
            </div>
            <div class="row mb-20 rep-profile__fields">
              <div class="col q-pa-sm">
                <zbm-telephone-with-validation v-model="phoneTelNumberInputModel"
                                               :input-width="$zb.enums.FormElWidthEnum.qFormElFull" countryCode="US"
                                               :placeholder="$t('gen_phone')" ref="phoneInputRef" />
              </div>
              <div class="col q-pa-sm">
                <q-select outlined v-model="statusModel" :options="statusOptions" :placeholder="$t('gen_status')" 
                          option-value="value" option-label="label" emit-value map-options />
              </div>
              <div class="col q-pa-sm"></div>
            </div>

            <div class="row items-center q-col-gutter-x-sm rep-profile__buttons">
              <div class="col-auto q-ml-auto">
                <zbm-skeleton-loader height="2.8571428571rem" width="3.1428571429rem">
                  <zbm-btn
                    @click="onCancel"
                    :color="$zb.enums.ButtonStdColorEnum.darkTealText"
                    :height="$zb.enums.ButtonHeightEnum.md"
                    :label="$t('gen_clear_filters')"
                    :data-testid="'btnCancel'"
                  />
                </zbm-skeleton-loader>
              </div>

              <div class="col-auto">
                <zbm-skeleton-loader height="2.8571428571rem" width="11.5714285714rem">
                  <zbm-btn
                    @click="onSubmit"
                    :color="$zb.enums.ButtonStdColorEnum.darkTeal"
                    :height="$zb.enums.ButtonHeightEnum.md"
                    :label="$t('gen_search')"
                    :type="$zb.enums.ButtonTypeEnum.submit"
                    :data-testid="'btnSave'"
                  />
                </zbm-skeleton-loader>
              </div>
            </div>
          </zbm-skeleton-loader>
        </q-form>
      </div>
    </div>

    <div v-if="showResults">
      <zbm-table
        :columns="inviteTableColumns"
        :rows="inviteTableRows"
        row-key="inviteId"
        expandable
        :hide-pagination="false"
        ref="inviteTable"
        v-model:pagination="initialPagination"
        :loading="loading"
        @request="onRequest"
      >
        <template v-slot:header="props">
          <q-tr :props="props">
            <q-th key="status" :props="props" class="text-left">{{ $t('gen_status') }}</q-th>
            <q-th key="firstName" :props="props" class="text-left">{{ $t('gen_first_name') }}</q-th>
            <q-th key="lastName" :props="props" class="text-left">{{ $t('gen_last_name') }}</q-th>
            <q-th key="email" :props="props" class="text-left">{{ $t('gen_email') }}</q-th>
            <q-th key="phoneNumber" :props="props" class="text-left">{{ $t('gen_phone') }}</q-th>
            <q-th key="lastSentDate" :props="props" class="text-left">{{ $t('invite_email_last_sent') }}</q-th>
          </q-tr>
        </template>
        <template v-slot:body="props">
          <q-tr :props="props" :key="`row-${props.row.inviteId}`" class="cursor-pointer" @click="onRowClick(props.row.inviteId)">
            <q-td key="status" :props="props" no-hover>
              {{ $t(`invite_status_${props.row.status.toLowerCase()}`) }}
            </q-td>
            <q-td key="firstName" :props="props" no-hover>
              {{ props.row.firstName }}
            </q-td>
            <q-td key="lastName" :props="props" no-hover>
              {{ props.row.lastName }}
            </q-td>
            <q-td key="email" :props="props" no-hover>
              {{ props.row.email }}
            </q-td>
            <q-td key="phoneNumber" :props="props" no-hover>
              {{ formatPhoneNumber(props.row.phoneCountryCode, props.row.phoneNumber, '') }}
            </q-td>
            <q-td key="lastSentDate" :props="props" no-hover>
              {{ props.row.lastSentDate }}
            </q-td>
          </q-tr>
        </template>
      </zbm-table>
    </div>
  </q-page>
</template>
